import { useEffect, useRef, useState } from "react";
import urls from "../utils/url";
import { KEYAR_WS_ROUTES } from "../utils/websocketRoutes";
import { createKeyarSocket } from "../utils/keyarSocket";

const PLACE_MAX_BACKOFF_MS = 15_000;
// A place-wide connection with no frame for this long is replaced by a fresh
// one, so a session that started while it was quiet (or a half-dead socket)
// can't leave the CMS silent until the page is reloaded.
const PLACE_IDLE_RECYCLE_MS = 60_000;

/**
 * Subscribes to a KEYAR live-vitals WebSocket feed for the lifetime of the
 * calling component, and forwards every parsed frame to onFrame.
 *
 * @param {'patient'|'place'} mode - 'patient' uses the per-patient route
 *   (spec §2.2.B); 'place' uses the facility/ward-scoped route (§2.2.A),
 *   which streams every active bed/device at that one place_id.
 * @param {string|number} id - patientId for 'patient' mode, placeId for 'place' mode.
 * @param {(frame: object) => void} onFrame - called with each parsed frame;
 *   the caller decides how to store it (Redux dispatch, local ref, etc).
 * @param {(status: string, detail?: object) => void} [onStatusChange] - optional,
 *   called alongside every internal status update, for a caller (e.g. a
 *   staleness sweep) that needs to know connection health without
 *   depending on this hook's own state/re-renders.
 * @param {typeof WebSocket} [WebSocketImpl] - injectable for testing.
 * @returns {{ status: 'connecting'|'open'|'reconnecting'|'error'|'closed', errorDetail: object|null }}
 */
export function useKeyarLiveData({ mode, id, onFrame, onStatusChange, WebSocketImpl }) {
    const [status, setStatus] = useState("connecting");
    const [errorDetail, setErrorDetail] = useState(null);
    const onFrameRef = useRef(onFrame);
    onFrameRef.current = onFrame;
    const onStatusChangeRef = useRef(onStatusChange);
    onStatusChangeRef.current = onStatusChange;

    useEffect(() => {
        if (!id) return undefined;

        const buildUrl = () => {
            const route = mode === "place" ? KEYAR_WS_ROUTES.PLACE(id) : KEYAR_WS_ROUTES.PATIENT(id);
            return `${urls.BETA_WS_URL}${route}`;
        };

        const { connect, close } = createKeyarSocket({
            buildUrl,
            onMessage: (frame) => onFrameRef.current(frame),
            onStatusChange: (nextStatus, detail) => {
                setStatus(nextStatus);
                setErrorDetail(nextStatus === "error" ? detail : null);
                onStatusChangeRef.current?.(nextStatus, detail);
            },
            // The place-wide feed must pick a new patient up quickly, so cap
            // its backoff; the per-patient feed keeps the full slow ladder
            // (it may never succeed for a patient still on the old app).
            ...(mode === "place"
                ? { maxDelayMs: PLACE_MAX_BACKOFF_MS, idleRecycleMs: PLACE_IDLE_RECYCLE_MS, alwaysRetry: true }
                : {}),
            ...(WebSocketImpl ? { WebSocketImpl } : {}),
        });

        connect();
        return () => close();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, id]);

    return { status, errorDetail };
}
